import cn from 'classnames'
import React, { FC, InputHTMLAttributes } from 'react'
import { CheckCircleIcon, CircleIcon } from 'components/icons'

// Explicitly separate our custom props from native input props
type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'type' | 'checked'
>

type CheckboxProps = NativeInputProps & {
  label?: string
  id: string
  checked: boolean
  onChange?: (newValue: boolean) => void
}

const Checkbox: FC<CheckboxProps> = ({
  checked,
  className,
  disabled = false,
  id,
  label,
  name,
  onChange,
  ...rest
}) => {
  const handleToggle = () => {
    if (!disabled) {
      onChange?.(!checked)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      handleToggle()
    }
  }

  return (
    <div
      role='checkbox'
      aria-checked={checked}
      aria-disabled={disabled}
      aria-labelledby={label ? `${id}-label` : undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      className={cn(
        'lh-input d-flex gap-1 align-items-center user-select-none',
        {
          'cursor-pointer': !disabled,
          'opacity-50 cursor-not-allowed': disabled,
        },
        className,
      )}
    >
      {/* Hidden native input for form compatibility */}
      <input
        type='checkbox'
        id={id}
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={() => {}} // Controlled by parent via custom onChange
        tabIndex={-1}
        className='position-absolute opacity-0 pointer-events-none'
        {...rest}
      />

      {checked ? (
        <CheckCircleIcon
          width={24}
          height={24}
          fill='#1977EC'
          aria-hidden='true'
        />
      ) : (
        <CircleIcon width={24} height={24} aria-hidden='true' />
      )}

      {label && (
        <label
          id={`${id}-label`}
          className='form-check-label cursor-pointer'
          htmlFor={id}
        >
          {label}
        </label>
      )}
    </div>
  )
}

Checkbox.displayName = 'Checkbox'
export default Checkbox
